import { useEffect, useState } from "react";
import { currencyLabel } from "../../lib/currency";
import useApi from "../../hooks/useApi";
import { Button, Input, LoadingSpinner } from "../../components/BaseComponents";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import useCleanup from "../../hooks/useCleanup";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import ISICSelector from "../../components/BaseComponents/ISICSelector/ISICSelector";
import InputCheckbox from "../../components/BaseComponents/InputCheckbox";
import InlineAlert from "../../components/BaseComponents/Alert/InlineAlert";
import NumericText from "../../components/BaseComponents/NumericText/NumericText";

type AdminCompanySummaryProps = {
    companyId: string;
}

const formatNumber = Intl.NumberFormat().format;

function AdminCompanySummary({ companyId }: AdminCompanySummaryProps) {
    const [data, setData] = useState<any>();
    const api = useApi();

    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteCofirmationOpen, setDeleteConfirmatinOpen] = useState(false);

    const [isUpdating, setIsUpdating] = useState(false);

    const [industryCodes, setIndustryCodes] = useState<string[]>([]);

    const [name, setName] = useState("");
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [tagError, setTagError] = useState("");
    // Issue #884: updateIndustryCodes/updateName/getData/onDelete previously had no catch at
    // all, so a rejecting write or read (including the now-rejecting READ_ONLY_ACCESS/
    // ACCESS_AUDIT_UNAVAILABLE notify-only codes - #857 item 6) produced an unhandled rejection,
    // left their loading flags stuck true, and never told the admin anything had gone wrong -
    // `updateTag` just above is the existing in-file reference pattern these now match.
    const [industryCodesError, setIndustryCodesError] = useState("");
    const [nameError, setNameError] = useState("");
    const [loadError, setLoadError] = useState("");
    const [loadedCompanyId, setLoadedCompanyId] = useState<string>();
    const [loadedApi, setLoadedApi] = useState<ReturnType<typeof useApi>>();
    const [deleteError, setDeleteError] = useState("");

    const updateIndustryCodes = async () => {
        setIsUpdating(true);
        setIndustryCodesError("");
        try {
            await api.put(`/admin/company/${companyId}`, {
                _csrf: await getCSRFToken(),
                company: {
                    industryCodes,
                }
            });
        } catch (err: any) {
            setIndustryCodesError(getApiErrorMessage(err, 'Failed to update the industry code. Please try again.'));
            return;
        } finally {
            setIsUpdating(false);
        }
    };

    const updateName = async () => {
        setIsUpdatingName(true);
        setNameError("");
        try {
            await api.put(`/admin/company/${companyId}`, {
                _csrf: await getCSRFToken(),
                company: {
                    name,
                }
            });
            getData();
        } catch (err: any) {
            setNameError(getApiErrorMessage(err, 'Failed to update the organisation name. Please try again.'));
            return;
        } finally {
            setIsUpdatingName(false);
        }
    };

    const updateTag = async (tag: 'isPublicBody' | 'isRegistered' | 'isFinancialInstitution', value: boolean) => {
        setTagError("");
        try {
            await api.put(`/admin/company/${companyId}`, {
                _csrf: await getCSRFToken(),
                company: { [tag]: value },
            });
        } catch (error: any) {
            setTagError(getApiErrorMessage(error, "Could not update the tag"));
            return;
        }
        getData();
    };

    const getData = useCleanup(() => {
        let cancelled = false;
        setLoadedCompanyId(companyId);
        setLoadedApi(api);
        setData(undefined);
        setLoadError("");
        const run = async () => {
            try {
                const result = await api.get(`admin/company/${companyId}/summary`);
                if (cancelled) return;
                setData(result.data);
                setIndustryCodes(result.data?.isicCode ? [result.data?.isicCode] : [])
                setName(result.data?.company?.name || "")
                setLoadError("");
            } catch (err: any) {
                if (cancelled) return;
                setLoadError(getApiErrorMessage(err, 'Failed to load this organisation. Please try again.'));
            }
        };
        run();
        return () => {
            cancelled = true;
        }
    })
    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, api])


    const onDeleteClick = () => {
        setDeleteConfirmatinOpen(true);
    }

    const onDelete = async () => {
        setIsDeleting(true);
        setDeleteConfirmatinOpen(false);
        setDeleteError("");
        try {
            await api.delete(`/admin/company/${companyId}/data`, {
                _csrf: await getCSRFToken()
            });
            getData();
        } catch (err: any) {
            // The confirmation dialog already closed above (pre-existing behaviour, unchanged) -
            // the error has to surface on the page itself, not inside the (now-closed) modal.
            setDeleteError(getApiErrorMessage(err, 'Failed to delete this organisation\'s data. Please try again.'));
            return;
        } finally {
            setIsDeleting(false);
        }
    }

    if (loadedCompanyId !== companyId || loadedApi !== api) {
        return <LoadingSpinner />
    }
    if (loadError) {
        return (
            <InlineAlert type='error' className='mt-4'>
                <p>{loadError}</p>
                <Button type='Primary' text='Retry organisation' onClick={getData} />
            </InlineAlert>
        )
    }
    if(!data) {
        return <LoadingSpinner />
    }
    return (
        <div>
            <dl className='mt-3 grid grid-cols-3 text-white'>
                <dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>Organisation Name</dt>
                <dd className='col-span-2 p-4 border-y border-charcoal-200'>
                    <div className="flex space-x-2 items-end">
                        { isUpdatingName ? (
                            <LoadingSpinner inline />
                        ) : (
                            <>
                                <div className="flex-grow">
                                    <Input
                                        type="text"
                                        name="companyName"
                                        label="Organisation Name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <Button type="Primary" text="Update" size="md"
                                        accessibilityLabel="organisation name"
                                        disabled={!name.trim() || name === data.company.name}
                                        onClick={() => updateName()}/>
                                </div>
                            </>
                        )}
                    </div>
                    { nameError && <InlineAlert type='error' className='mt-4'>{ nameError }</InlineAlert> }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>iSumio Internal ID</dt>
                <dd className='col-span-2 p-4 border-y border-charcoal-200'>
                    { data.company.id }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>Organisation number</dt>
                <dd className='col-span-2 p-4 border-y border-charcoal-200'>
                    { data.company.number || '-'}
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Organisation tags</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    { [
                        { tag: 'isPublicBody', column: 'is_public_body', label: 'Public body' },
                        { tag: 'isRegistered', column: 'is_registered', label: 'Registered' },
                        { tag: 'isFinancialInstitution', column: 'is_financial_institution', label: 'Financial institution' },
                    ].map(({ tag, column, label }) => (
                        <label key={tag} className='flex items-center space-x-2'>
                            <InputCheckbox
                                checked={!!data.company[column]}
                                disabled={data.company.category === 'fictitious'}
                                onChange={(e) => updateTag(tag as 'isPublicBody' | 'isRegistered' | 'isFinancialInstitution', e.target.checked)}
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                    { tagError && <InlineAlert type='warning' className='mt-6'>{ tagError }</InlineAlert> }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Jurisdiction</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    {data.company.jurisdiction_code || '-'}
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Currency Code</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    { currencyLabel(data.company.currency_code) }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Address</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    { data.company.address || '-' }
                </dd>

                <dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>ISIC Code</dt>
                <dd className='col-span-2 p-4 border-b border-charcoal-200'>
                    <div className="flex space-x-2">
                        { isUpdating ? (
                            <LoadingSpinner inline />
                        ) : (
                            <>
                                <div className="flex-grow text-charcoal-900">
                                    <ISICSelector
                                        darkMode={true}
                                        min={1}
                                        max={1}
                                        value={industryCodes}
                                        updateHandler={setIndustryCodes}
                                    />
                                </div>
                                <div>
                                    <Button type="Primary" text="Update" size="md"
                                        onClick={() => updateIndustryCodes()}/>
                                </div>
                            </>
                        )}
                    </div>
                    { industryCodesError && <InlineAlert type='error' className='mt-4'>{ industryCodesError }</InlineAlert> }
                </dd>
            </dl>
            <div className="text-white">Organisation Data</div>
            { isDeleting 
            ? (
                <LoadingSpinner/>
            ) : (
                <dl className='mt-3 grid grid-cols-3 text-white'>
                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Accounting Periods</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        <NumericText value={formatNumber(data.periodCount || 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Suppliers</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        <NumericText value={formatNumber(data.supplierCount || 0)} />
                    </dd>


                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Items</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        <NumericText value={formatNumber(data.itemCount|| 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Purchases</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.purchaseCount|| 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Projects</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.projectCount || 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Reports</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.reportCount || 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Portfolios</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.portfolioCount || 0)} />
                    </dd>
                </dl>
            )}
            { deleteError && <InlineAlert type='error' className='mt-4'>{ deleteError }</InlineAlert> }
            <Button
                additionalClass="mt-4" type="Primary" text="Delete Organisation Data"
                onClick={onDeleteClick}
                disabled={isDeleting}
            />
            <ConfirmationModal
                open={deleteCofirmationOpen}
                onCancel={() => setDeleteConfirmatinOpen(false)}
                onConfirm={onDelete}
                requireTypedConfirmation="delete"
                title="Confirm Deletion of Organisation Data"
            >
                <p className="text-white mt-8">
                    Confirm deleting the following data for <strong>{data.company.name}</strong>?
                </p>

                <dl className='mt-3 grid grid-cols-3 text-white'>
                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Accounting Periods</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        <NumericText value={formatNumber(data.periodCount || 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Suppliers</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        <NumericText value={formatNumber(data.supplierCount || 0)} />
                    </dd>


                    <dt className='bg-charcoal-400 p-1 border-y border-charcoal-200'>Items</dt>
                    <dd className='col-span-2 p-1 border-y border-charcoal-200'>
                        <NumericText value={formatNumber(data.itemCount|| 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Purchases</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.purchaseCount|| 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Projects</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.projectCount || 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Reports</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.reportCount || 0)} />
                    </dd>

                    <dt className='bg-charcoal-400 p-1 border-b border-charcoal-200'>Portfolios</dt>
                    <dd className='col-span-2 p-1 border-b border-charcoal-200'>
                        <NumericText value={formatNumber(data.portfolioCount || 0)} />
                    </dd>
                </dl>
            </ConfirmationModal>
        </div>
    )
}

export default AdminCompanySummary;
